import { isDefined } from '@sgme/fp';

import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import { dayAndTimeToDate } from '@/App/tiles/orders/utils.ts';
import { mapOrderTypeToWebApiPathComponent } from '@/store/orders/algoOrders/helper.ts';
import type { AlgoOrderRequest, Speed } from '@/store/orders/algoOrders/state.ts';
import type { ValidateOrderResponse } from '@/store/orders/state.ts';
import { getDateNow } from '@/utils/date.ts';
import { toBase64 } from '@/utils/toBase64.ts';

type BaseValidateAlgoOrderBody = {
  isGtc: boolean;
  bdrId: string;
  amountInCcy1: number | undefined;
  amountInCcy2: number | undefined;
  ccyPair: string;
  way: 'buy' | 'sell';
  base64Emails: string;
  localTimeWithOffSet: number;
  maturityDateString?: string;
  startDateUtc?: string;
  endDateUtc?: string;
  limitPrice?: string;
  isNdf: boolean;
  liquidityPool: string;
};

type ValidateTwapOrderBody = BaseValidateAlgoOrderBody & {
  clipSize?: number;
  spreadCapture: boolean;
  clippingMode: string;
  dodging: boolean;
};

type ValidateFalconOrderBody = BaseValidateAlgoOrderBody & {
  speed: Speed;
};

type ValidateNightjarOrderBody = BaseValidateAlgoOrderBody & {
  speed: Speed;
  alphaSeeker: boolean;
};

export type ValidateAlgoOrderBody = ValidateTwapOrderBody | ValidateFalconOrderBody | ValidateNightjarOrderBody;

export type ValidateOrderResponseError = {
  Status: number;
  Title: string;
};

export const validateAlgo = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<ValidateOrderResponse, AlgoOrderRequest>({
    query: (request) => ({
      url: `/oms/validate/${mapOrderTypeToWebApiPathComponent(request.orderType)}`,
      method: 'POST',
      body: generateBody(request),
      headers: {
        // without this header, the response is a string
        // instead of JSON, even if the value is "application/json"
        accept: '*/*',
      },
    }),
    transformErrorResponse: (baseQueryReturnValue) => {
      if (baseQueryReturnValue && typeof baseQueryReturnValue === 'object' && 'data' in baseQueryReturnValue) {
        if (typeof baseQueryReturnValue.data === 'string') {
          return JSON.parse(baseQueryReturnValue.data) as ValidateOrderResponse;
        }

        return baseQueryReturnValue.data as ValidateOrderResponse;
      }
    },
  });
};

const generateBody = (order: AlgoOrderRequest): ValidateAlgoOrderBody => {
  const commonBody = {
    isGtc: order.expiryDate.goodTillDate,
    bdrId: String(order.clientId),
    ccyPair: `${order?.currency1}/${order?.currency2}`,
    way: order.way ?? 'buy',
    amountInCcy1: order.notionalCurrencyIndex === 1 ? order.notional : undefined,
    amountInCcy2: order.notionalCurrencyIndex === 2 ? order.notional : undefined,
    liquidityPool: order.liquidityPool.length > 0 ? order.liquidityPool.join(',') : '',
    isNdf: order.isNdf,
    maturityDateString: !order.isNdf ? order.tenor : undefined,
    startDateUtc: order.startDate.isNow
      ? getDateNow().toISOString()
      : dayAndTimeToDate(order.startDate.date, order.startDate.time),
    limitPrice: isDefined(order.limitPrice) ? String(order.limitPrice) : undefined,
    endDateUtc: !order.expiryDate.goodTillDate
      ? dayAndTimeToDate(order.expiryDate.date, order.expiryDate.time)
      : undefined,
    base64Emails: isDefined(order?.notificationEmails?.emails)
      ? toBase64(order?.notificationEmails.emails.join(';'))
      : '',
    localTimeWithOffSet: order.clockOffset !== undefined ? Date.now() + order.clockOffset : 0,
  };

  switch (order.orderType) {
    case 'Twap+Order':
      return {
        ...commonBody,
        clippingMode: order.clippingMode,
        spreadCapture: order.spreadCapture,
        clipSize: order.clippingMode === 'Manual' ? order.clipSize : undefined,
        dodging: order.randomize,
      };
    case 'FalconOrder':
      return {
        ...commonBody,
        speed: order.speed,
      };
    case 'NightjarOrder':
      return {
        ...commonBody,
        speed: order.speed,
        alphaSeeker: order.alphaSeeker,
      };
    default: {
      const exhaustiveCheck: never = order;
      return exhaustiveCheck as ValidateAlgoOrderBody;
    }
  }
};
